import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
  useTheme,
} from '@mui/material';
import { CaretDown, Plus, SquaresFour } from '@phosphor-icons/react';
import { StyledTextField } from '../../Common/StyledTextField';
import { useDarkMode } from '../../../hooks/useDarkMode';
import { useAuthStore } from '../../../store/storeAuth';
import { useDashboardManagerContext } from '../../../views/DashboardBeta/Context/DashboardManagerContext';
import { DashboardMeta } from '../../../views/DashboardBeta/hooks/useDashboardManager';
import { CreateDashboardModal } from './CreateDashboardModal';
import { useShowDashboardVisibilityOptions } from './hooks/useShowDashboardVisibilityOptions';
import { DashboardSelectorItem } from './DashboardSelectorItem';

export const DashboardSelector: React.FC = () => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const authUser = useAuthStore(state => state.user);
  const showDashboardVisibilityOptions =
    useShowDashboardVisibilityOptions(authUser);

  const {
    dashboards,
    activeDashboard,
    isLoading,
    isCreating,
    createDashboard,
    selectDashboard,
    renameDashboard,
    deleteDashboard,
    publishDashboard,
    setDefaultDashboard,
  } = useDashboardManagerContext();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [dashboardToRename, setDashboardToRename] =
    useState<DashboardMeta | null>(null);
  const [newName, setNewName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] =
    useState<DashboardMeta | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectDashboard = (dashboardId: string) => {
    selectDashboard(dashboardId);
    handleClose();
  };

  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
    handleClose();
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const handleConfirmCreate = async (name: string) => {
    try {
      await createDashboard(name);
      handleCloseCreateDialog();
    } catch (error) {
      console.error('Failed to create dashboard:', error);
    }
  };

  const handleOpenRenameModal = (dashboard: DashboardMeta) => {
    setDashboardToRename(dashboard);
    setNewName(dashboard.name);
    setRenameModalOpen(true);
  };

  const handleCloseRenameModal = () => {
    setRenameModalOpen(false);
    setDashboardToRename(null);
    setNewName('');
  };

  const handleConfirmRename = async () => {
    if (dashboardToRename && newName.trim()) {
      setIsRenaming(true);
      try {
        await renameDashboard(dashboardToRename.id, newName.trim());
      } catch (error) {
        console.error('Failed to rename dashboard:', error);
      } finally {
        setIsRenaming(false);
        handleCloseRenameModal();
      }
    }
  };

  const handleOpenDeleteDialog = (dashboard: DashboardMeta) => {
    setDashboardToDelete(dashboard);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDashboardToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (dashboardToDelete) {
      setIsDeleting(true);
      try {
        await deleteDashboard(dashboardToDelete.id);
      } catch (error) {
        console.error('Failed to delete dashboard:', error);
      } finally {
        setIsDeleting(false);
        handleCloseDeleteDialog();
      }
    }
  };

  const handlePublish = async (dashboard: DashboardMeta) => {
    try {
      const shouldPublish = dashboard.status !== 'published';
      await publishDashboard(dashboard.id, shouldPublish);
    } catch (error) {
      console.error('Failed to update dashboard status:', error);
    }
  };

  const handleSetDefault = async (dashboard: DashboardMeta) => {
    try {
      await setDefaultDashboard(dashboard.id);
    } catch (error) {
      console.error('Failed to set default dashboard:', error);
    }
  };

  return (
    <>
      <Box
        component='button'
        onClick={handleClick}
        sx={{
          display: 'flex',
          padding: '8px',
          height: '33px',
          alignItems: 'center',
          gap: '8px',
          borderRadius: '4px',
          border: `1px solid ${
            isDarkMode ? palette.gray[700] : palette.neutral[200]
          }`,
          backgroundColor: isDarkMode ? palette.gray[800] : '#FFFFFF',
          cursor: 'pointer',
          flexShrink: 0,
          '&:hover': {
            backgroundColor: isDarkMode
              ? palette.gray[700]
              : palette.neutral[100],
          },
        }}
      >
        <SquaresFour
          size={16}
          color={isDarkMode ? palette.gray[200] : '#000000'}
        />
        <Typography
          sx={{
            color: isDarkMode ? palette.gray[200] : '#000000',
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: 'Roboto',
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '143%',
            letterSpacing: '0.17px',
            whiteSpace: 'nowrap',
            maxWidth: '150px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {isLoading
            ? 'Loading...'
            : activeDashboard?.name || 'Select Dashboard'}
        </Typography>
        <CaretDown
          size={16}
          color={isDarkMode ? palette.gray[400] : '#000000'}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              display: 'flex',
              flexDirection: 'column',
              padding: '8px',
              marginTop: '4px',
              borderRadius: '8px',
              border: `1px solid ${
                isDarkMode ? palette.gray[700] : palette.gray[300]
              }`,
              backgroundColor: isDarkMode
                ? palette.gray[900]
                : palette.gray.A100,
              boxShadow: 'none',
              minWidth: '260px',
              maxWidth: '320px',
              overflowX: 'auto',
            },
          },
        }}
      >
        <Typography
          sx={{
            padding: '8px 12px',
            color: isDarkMode ? palette.gray[400] : palette.gray[500],
            fontFamily: 'Roboto',
            fontSize: '11px',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Your Dashboards
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            overflow: 'auto',
            maxHeight: '320px',
          }}
        >
          {dashboards.map(dashboard => (
            <DashboardSelectorItem
              key={dashboard.id}
              dashboard={dashboard}
              isActive={dashboard.id === activeDashboard?.id}
              onSelect={() => handleSelectDashboard(dashboard.id)}
              onRename={handleOpenRenameModal}
              onDelete={handleOpenDeleteDialog}
              onPublish={handlePublish}
              onSetDefault={handleSetDefault}
              canDelete={dashboards.length > 1}
            />
          ))}
        </Box>

        <Box
          sx={{
            borderTop: `1px solid ${
              isDarkMode ? palette.gray[700] : palette.gray[200]
            }`,
            marginTop: '8px',
            paddingTop: '8px',
          }}
        >
          <ListItemButton
            onClick={handleOpenCreateDialog}
            disabled={isCreating}
            sx={{
              display: 'flex',
              padding: '4px 8px',
              alignItems: 'center',
              gap: '8px',
              alignSelf: 'stretch',
              borderRadius: '4px',
              opacity: isCreating ? 0.6 : 1,
              '&:hover': {
                backgroundColor: isDarkMode
                  ? palette.gray[800]
                  : palette.gray[200],
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {isCreating ? (
                <CircularProgress size={20} />
              ) : (
                <Plus
                  size={20}
                  color={isDarkMode ? palette.gray[400] : palette.gray[500]}
                />
              )}
            </ListItemIcon>
            <ListItemText
              primary={isCreating ? 'Creating...' : 'Create New'}
              primaryTypographyProps={{
                sx: {
                  color: isDarkMode ? palette.gray[300] : palette.gray[600],
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '150%',
                  letterSpacing: '0.15px',
                  margin: 0,
                },
              }}
            />
          </ListItemButton>
        </Box>
      </Popover>

      <Dialog
        open={renameModalOpen}
        onClose={handleCloseRenameModal}
        maxWidth='xs'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? palette.gray[900] : '#fff',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          sx={{ color: isDarkMode ? palette.gray[100] : palette.gray[900] }}
        >
          Rename Dashboard
        </DialogTitle>
        <DialogContent>
          <StyledTextField
            autoFocus
            fullWidth
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder='Dashboard name'
            variant='outlined'
            size='small'
            sx={{ marginTop: '8px' }}
            onKeyDown={e => {
              if (e.key === 'Enter' && newName.trim()) {
                handleConfirmRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseRenameModal}
            disabled={isRenaming}
            sx={{ color: isDarkMode ? palette.gray[400] : undefined }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRename}
            disabled={!newName.trim() || isRenaming}
            variant='contained'
          >
            {isRenaming ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateDashboardModal
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        onCreate={handleConfirmCreate}
        isCreating={isCreating}
        showAccessPermissionOptions={showDashboardVisibilityOptions}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth='xs'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? palette.gray[900] : '#fff',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          sx={{ color: isDarkMode ? palette.gray[100] : palette.gray[900] }}
        >
          Delete Dashboard
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ color: isDarkMode ? palette.gray[400] : palette.gray[600] }}
          >
            Are you sure you want to delete "{dashboardToDelete?.name}"? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={isDeleting}
            sx={{ color: isDarkMode ? palette.gray[400] : undefined }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            variant='contained'
            color='error'
          >
            {isDeleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
